/**
 * Configuration globale d'Axios avec gestion des erreurs et cleanup automatique
 * 
 * Ce module configure des intercepteurs globaux pour :
 * - Gérer automatiquement les erreurs d'annulation (AbortError, CanceledError)
 * - Formatter les erreurs de manière cohérente
 * - Empêcher les crashes "Objects are not valid as a React child"
 * - Logger les erreurs de manière standardisée
 */

import axios from 'axios';
import { toast } from 'sonner';

// Configuration de base
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE_URL = `${BACKEND_URL}/api`;

/**
 * Crée une instance Axios configurée avec les intercepteurs
 */
export const createAxiosInstance = () => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 secondes
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Intercepteur de requête - Ajouter le token si disponible
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Intercepteur de réponse - Gérer les erreurs globalement
  instance.interceptors.response.use(
    (response) => {
      // Réponse réussie, la retourner telle quelle
      return response;
    },
    (error) => {
      // Gérer les différents types d'erreurs

      // 1. Erreurs d'annulation (AbortController) - NE PAS afficher d'erreur
      if (
        error.name === 'AbortError' ||
        error.name === 'CanceledError' ||
        axios.isCancel(error)
      ) {
        console.debug('Request was cancelled:', error.message);
        return Promise.reject({ cancelled: true, original: error });
      }

      // 2. Erreur réseau (pas de connexion)
      if (!error.response) {
        console.error('Network error:', error.message);
        toast.error('Erreur de connexion au serveur');
        return Promise.reject({
          type: 'network',
          message: 'Impossible de se connecter au serveur',
          original: error,
        });
      }

      // 3. Erreur de timeout
      if (error.code === 'ECONNABORTED') {
        console.error('Request timeout:', error.message);
        toast.error('La requête a pris trop de temps');
        return Promise.reject({
          type: 'timeout',
          message: 'Délai d\'attente dépassé',
          original: error,
        });
      }

      // 4. Erreurs HTTP avec réponse du serveur
      const status = error.response?.status;
      const data = error.response?.data;

      // Formatter l'erreur pour éviter les objets React non-renderable
      const errorMessage =
        typeof data === 'string'
          ? data
          : data?.detail || data?.message || error.message || 'Une erreur est survenue';

      // Gérer les cas spécifiques par code de statut
      switch (status) {
        case 401:
          console.warn('Unauthorized - Token may be expired');
          // Ne pas afficher de toast pour éviter les doublons
          // Le composant AuthContext s'en chargera
          break;

        case 403:
          console.warn('Forbidden - Insufficient permissions');
          toast.error('Vous n\'avez pas les permissions nécessaires');
          break;

        case 404:
          console.warn('Resource not found:', error.config?.url);
          toast.error('Ressource non trouvée');
          break;

        case 422:
          console.warn('Validation error:', errorMessage);
          // Afficher le message de validation détaillé
          toast.error(errorMessage);
          break;

        case 500:
        case 502:
        case 503:
          console.error('Server error:', errorMessage);
          toast.error('Erreur serveur, veuillez réessayer');
          break;

        default:
          console.error('HTTP error:', status, errorMessage);
          toast.error(errorMessage);
      }

      // Retourner une erreur formatée
      return Promise.reject({
        type: 'http',
        status,
        message: errorMessage,
        data,
        original: error,
      });
    }
  );

  return instance;
};

// Instance Axios globale par défaut
export const axiosInstance = createAxiosInstance();

// Export par défaut pour faciliter le remplacement de 'axios' dans les imports
export default axiosInstance;

/**
 * Hook-like function pour créer une requête avec AbortController automatique
 * Utilisation:
 * 
 * const fetchData = createAbortableRequest((signal) => 
 *   axios.get('/api/data', { signal })
 * );
 * 
 * // Dans un useEffect:
 * useEffect(() => {
 *   const { request, abort } = fetchData();
 *   request.then(data => setData(data));
 *   return abort;
 * }, []);
 */
export const createAbortableRequest = (requestFn) => {
  return () => {
    const abortController = new AbortController();

    const request = requestFn(abortController.signal).catch((error) => {
      // Ne pas propager les erreurs d'annulation
      if (error?.cancelled) {
        return null;
      }
      throw error;
    });

    return {
      request,
      abort: () => abortController.abort(),
      signal: abortController.signal,
    };
  };
};

/**
 * Wrapper pour les requêtes qui gère automatiquement le montage/démontage
 * 
 * @param {Function} requestFn - Fonction qui retourne une promesse Axios
 * @param {Function} onSuccess - Callback appelé en cas de succès
 * @param {Function} onError - Callback appelé en cas d'erreur (optionnel)
 * @param {Object} isMountedRef - Ref pour vérifier si le composant est monté
 * @returns {Function} - Fonction d'annulation
 */
export const safeRequest = async (requestFn, onSuccess, onError, isMountedRef = { current: true }) => {
  try {
    const response = await requestFn();
    
    // Ne mettre à jour l'état que si le composant est toujours monté
    if (isMountedRef.current && onSuccess) {
      onSuccess(response.data);
    }
    
    return response.data;
  } catch (error) {
    // Ignorer les erreurs d'annulation
    if (error?.cancelled) {
      return null;
    }

    // Appeler le callback d'erreur si fourni et si le composant est monté
    if (isMountedRef.current && onError) {
      onError(error);
    }

    throw error;
  }
};

/**
 * Helper pour créer une configuration de requête avec timeout et signal
 */
export const createRequestConfig = (token, signal, additionalConfig = {}) => {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    signal,
    timeout: 15000, // 15 secondes par défaut
    ...additionalConfig,
  };
};
